import {useAuth0} from '@auth0/auth0-react';
import {useDispatch, useSelector} from 'react-redux';
import React, {useEffect, useRef, useState} from 'react';
import {Switch} from 'react-router-dom';
import loadable from '@loadable/component';

import {ROUTE_CONFIG} from 'constants/app-routes';
import FacilitySidebar from 'common/facility-sidebar/FacilitySidebar';
import Config from 'hooks/config';
import {loadConfig} from 'utils/startup';
import TrackingUtils from 'utils/tracking';
import SplashScreen from 'common/splash-screen/SplashScreen';
import {getInitialFacilities} from 'pages/facilities/store/facilities-actions';
import {
    selectSelectedFacilityId,
    selectTotalFacilities,
} from 'pages/facilities/store/facilities-selectors';
import useAuthAPISetup from 'common/auth/useAuthAPISetup';
import RouteRenderer from 'common/app/RouteRenderer';

const GDPRModal = loadable(() => import('common/gdpr/GDPRModal'));
const MaintenanceDisplay = loadable(
    () => import('common/errors/maintenance-display/MaintenanceDisplay')
);

import 'sass/main.scss';

import {FLAGS, isFeatureEnabled} from 'utils/featureFlags';
import {Box, useBreakpointValue} from '@spothero/ui';
import MotionBox from 'common/animation/MotionBox';
import {useAnimationContext} from 'common/animation/AnimationContext';
import TopNavHeader from 'common/top-nav-header/TopNavHeader';
import NoFacilitySelected from 'common/errors/NoFacilitySelected';
import {getExperimentsData} from 'store/experiments/experiments-actions';

const animationProps = (collapsed) => ({
    style: {
        originX: 1,
        originY: 0.5,
    },
    initial: false,
    animate: {
        left: collapsed ? '4.375rem' : '21.875rem',
        transition: {
            type: 'spring',
            duration: 0.5,
            bounce: 0,
            delay: collapsed ? 0.125 : 0,
        },
    },
});

const App = () => {
    const dispatch = useDispatch();
    const headerRef = useRef();
    const {authAPIReady} = useAuthAPISetup();
    const {isLoading, isAuthenticated} = useAuth0();
    const [showGDPRModal, setShowGDPRModal] = useState(false);
    const [userCountryCode, setUserCountryCode] = useState(null);
    const {data: userData, isPending: userPending} = useSelector(
        (state) => state.user
    );
    const facilityCount = useSelector(selectTotalFacilities);
    const selectedFacilityId = useSelector(selectSelectedFacilityId);
    const showMaintenanceScreen = isFeatureEnabled(
        FLAGS.maintenanceScreenEnabled
    );
    const {
        collapsed: [collapsed],
    } = useAnimationContext();

    const isMobile = useBreakpointValue({base: true, tablet: false});

    useEffect(() => {
        if (isLoading || !isAuthenticated) {
            return;
        }

        loadConfig((values) => Config.set(values))
            .then(async () => {
                const countryCode = Config.cloudfrontViewerCountryCode;

                if (
                    countryCode &&
                    countryCode !== 'US' &&
                    countryCode !== 'CA'
                ) {
                    setShowGDPRModal(true);
                    setUserCountryCode(countryCode);
                }

                try {
                    await TrackingUtils.init();
                } catch (error) {
                    console.error('Failed to initialize tracking:', error);
                }
            })
            .catch((error) => {
                console.error('Failed to load configuration:', error);
            });
    }, [isAuthenticated, isLoading]);

    // Load user data and facilities for the user
    useEffect(() => {
        if (!authAPIReady || !isAuthenticated || !userData?.user_id) {
            return;
        }

        try {
            dispatch(getInitialFacilities()).unwrap();
        } catch (err) {
            console.error('Failed to load facilities.', err);
        }
    }, [isAuthenticated, authAPIReady, userData, dispatch]);

    // Initializes analytics and experiments. Split from other useEffect blocks because of the many deps
    useEffect(() => {
        if (!isAuthenticated || userPending || !userData?.user_id) {
            return;
        }

        dispatch(getExperimentsData())
            .unwrap()
            .then(() => {
                TrackingUtils.setUserAttributes(userData);
            });
    }, [userPending, isAuthenticated, userData, dispatch]);

    if (showMaintenanceScreen) {
        return <MaintenanceDisplay />;
    }

    if (isAuthenticated && facilityCount === 0) {
        return <SplashScreen />;
    }

    const onGDPRModalClose = () => {
        setShowGDPRModal(false);
    };

    return (
        <Box
            position="relative"
            display="flex"
            flexDirection="column"
            minHeight="100vh"
        >
            <TopNavHeader ref={headerRef} userData={userData} />
            {!isMobile && <FacilitySidebar userData={userData} />}
            <MotionBox
                layout
                role="main"
                marginTop={{base: 20, tablet: 14}}
                width="fill-available"
                position={{base: 'unset', tablet: 'absolute'}}
                height={{base: 'unset', desktop: 'calc(100% - 60px)'}}
                display={{base: 'block', desktop: 'flex'}}
                flexDirection="column"
                {...animationProps(collapsed)}
            >
                <Switch>
                    {!selectedFacilityId ? (
                        <RouteRenderer
                            component={NoFacilitySelected}
                            protected
                        />
                    ) : (
                        ROUTE_CONFIG.map((route, index) => {
                            return <RouteRenderer key={index} {...route} />;
                        })
                    )}
                </Switch>
            </MotionBox>

            <GDPRModal
                isOpen={showGDPRModal}
                onClose={onGDPRModalClose}
                countryCode={userCountryCode}
            />
        </Box>
    );
};

export default App;
